// Note: Keycloak token will be passed in from components using useKeycloak hook

export interface LexMessage {
  id: string;
  sender: 'user' | 'lex';
  message: string;
  timestamp: Date;
  metadata?: {
    intent?: string;
    confidence?: number;
    actions?: any[];
    documentContext?: {
      documentId?: string;
      documentType?: string;
      documentName?: string;
    };
    matterContext?: {
      matterId?: string;
      matterName?: string;
      clientId?: string;
    };
    moduleContext?: {
      application: 'documents' | 'matters' | 'billing' | 'calendar' | 'contacts' | 'admin';
      currentView?: string;
    };
    /** Pipeline metadata for feedback classification (v1.2) */
    intentCategory?: string;
    toolId?: string;
    toolSuccess?: boolean;
  };
}

export interface LexContext {
  application: 'documents' | 'matters' | 'billing' | 'calendar' | 'contacts' | 'admin';
  documentId?: string;
  documentType?: string;
  documentContent?: string;
  matterId?: string;
  matterLabel?: string;
  clientId?: string;
  clientName?: string;
  userName?: string;
  currentView?: string;
  pluginIds?: string[];
  // When loading from history, this is the DB session id to append to
  sessionId?: string;
  // Optional current editor selection context for coreference/focus
  editorSelection?: { text: string; sectionPath?: string; start?: number; end?: number };
}

export interface LexResponse {
  success: boolean;
  message: string;
  intent?: string;
  confidence?: number;
  actions?: any[];
  telemetry?: any;
  metadata?: {
    intent?: string;
    confidence?: number;
    processingTime?: number;
  };
}

/**
 * Universal Lex Client - Single service for all Lex interactions across EdgeLex platform
 * 
 * This service provides a unified interface for all frontend components to interact
 * with the Universal Lex Orchestrator backend. It handles:
 * - Context-aware message sending
 * - Automatic authentication
 * - Module context switching
 * - Conversation continuity
 */
export class UniversalLexClient {
  private baseUrl: string;
  private conversationId: string | null = null;
  private currentContext: LexContext | null = null;

  constructor() {
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://app.webelievesoftware.com/api/v1'
      : 'http://localhost:3000/api/v1';
  }

  /**
   * Set the current module context for Lex
   * This allows Lex to understand what part of EdgeLex the user is working in
   */
  setContext(context: LexContext, userToken?: string): void {
    // Preserve an existing sessionId if the incoming context does not include one
    const preservedSessionId = (context as any)?.sessionId || (this.currentContext as any)?.sessionId;
    this.currentContext = { ...context, ...(preservedSessionId ? { sessionId: preservedSessionId } : {}) } as LexContext;
    
    // Generate conversation ID based on context for continuity
    // Use stable IDs by default so a thread can build multi‑turn memory.
    // Fresh (timestamped) IDs are reserved for explicit "Start Fresh" action.
    this.conversationId = this.generateConversationId(this.currentContext, false, userToken);
    
    console.log('🎯 [UniversalLexClient] Context updated:', {
      application: this.currentContext.application,
      conversationId: this.conversationId,
      hasDocument: !!this.currentContext.documentId,
      hasMatter: !!this.currentContext.matterId
    });
  }

  /**
   * Send a message to Lex with full context awareness
   */
  async sendMessage(message: string, authToken?: string): Promise<LexResponse> {
    if (!this.currentContext) {
      throw new Error('Lex context not set. Call setContext() before sending messages.');
    }

    if (!authToken) {
      throw new Error('Authentication token required');
    }

    try {
      // Prefer a real DMS document session when available; otherwise fall back to conversationId
      const sessionIdToUse = (this.currentContext as any)?.sessionId || this.conversationId;
      const requestPayload = {
        message,
        sessionId: sessionIdToUse,  // Backend expects a session identifier; align with DMS session when present
        context: this.currentContext,
        pluginIds: this.currentContext?.pluginIds || []  // Include pluginIds from context
      };

      console.log('📤 [UniversalLexClient] Sending message to Universal Orchestrator:', {
        message: message.substring(0, 100) + (message.length > 100 ? '...' : ''),
        sessionId: sessionIdToUse,
        application: this.currentContext.application,
        pluginIds: this.currentContext?.pluginIds
      });

      const response = await fetch(`${this.baseUrl}/lex-agent/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error(`Lex request failed: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      
      console.log('📥 [UniversalLexClient] Received response from Universal Orchestrator:', {
        success: responseData.success,
        intent: responseData.metadata?.intent,
        confidence: responseData.metadata?.confidence,
        hasActions: responseData.actions?.length > 0
      });

      return {
        success: true,
        message: responseData.response || responseData.message, // Backend uses 'response' field
        intent: responseData.intent,
        confidence: responseData.confidence,
        actions: responseData.actions || [],
        telemetry: responseData.telemetry || {},
        metadata: responseData.metadata || {}
      };

    } catch (error) {
      console.error('🚨 [UniversalLexClient] Error sending message:', error);
      
      return {
        success: false,
        message: 'I apologize, but I encountered an error processing your request. Please try again.',
        metadata: {
          intent: 'error',
          confidence: 0
        }
      };
    }
  }

  /**
   * Load conversation history for the current context
   */
  async loadConversationHistory(authToken?: string): Promise<LexMessage[]> {
    if (!this.currentContext || !authToken) {
      console.warn('🚨 [UniversalLexClient] Cannot load history: missing context or auth token');
      return [];
    }

    try {
      console.log('📜 [UniversalLexClient] Loading conversation history for:', this.conversationId);

      const response = await fetch(`${this.baseUrl}/lex-agent/conversation/${this.conversationId}/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        console.warn('🚨 [UniversalLexClient] Failed to load conversation history:', response.status);
        return [];
      }

      const data = await response.json();
      const payload = data?.data?.conversationHistory;
      
      // Convert backend conversation history to frontend message format
      const messages: LexMessage[] = [];
      
      if (Array.isArray(payload)) {
        let messageIndex = 0;
        for (const item of payload) {
          messageIndex++;
          if (item.type === 'intent' && item.content) {
            messages.push({
              id: `user-${this.conversationId}-${messageIndex}`,
              sender: 'user',
              message: item.content,
              timestamp: new Date(item.timestamp || Date.now()),
              metadata: { intent: item.category, confidence: item.confidence, ...(item.metadata || {}) }
            });
          }
          if (item.type === 'response' && item.content) {
            messages.push({
              id: `lex-${this.conversationId}-${messageIndex}`,
              sender: 'lex', 
              message: item.content,
              timestamp: new Date(item.timestamp || Date.now()),
              metadata: item.metadata
            });
          }
        }
      }

      console.log('✅ [UniversalLexClient] Loaded conversation history:', messages.length, 'messages');
      return messages;

    } catch (error) {
      console.error('🚨 [UniversalLexClient] Error loading conversation history:', error);
      return [];
    }
  }

  /**
   * Load specific conversation history by conversation ID
   */
  async loadSpecificConversation(conversationId: string, authToken: string): Promise<LexMessage[]> {
    if (!authToken) {
      console.warn('🚨 [UniversalLexClient] Cannot load specific conversation: missing auth token');
      return [];
    }

    try {
      console.log('📜 [UniversalLexClient] Loading specific conversation:', conversationId);

      const response = await fetch(`${this.baseUrl}/lex-agent/conversation/${conversationId}/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        console.warn('🚨 [UniversalLexClient] Failed to load specific conversation:', response.status);
        return [];
      }

      const data = await response.json();
      
      // Convert backend conversation history to frontend message format
      const messages: LexMessage[] = [];
      
      if (data.success && data.data && data.data.conversationHistory && Array.isArray(data.data.conversationHistory)) {
        let messageIndex = 0;
        for (const historyItem of data.data.conversationHistory) {
          messageIndex++;
          
          // Add user intent as message
          if (historyItem.type === 'intent' && historyItem.content) {
            messages.push({
              id: `user-${conversationId}-${messageIndex}`,
              sender: 'user',
              message: historyItem.content,
              timestamp: new Date(historyItem.timestamp || Date.now()),
              metadata: {
                intent: historyItem.category,
                confidence: historyItem.confidence
              }
            });
          }
          
          // Add assistant response
          if (historyItem.type === 'response' && historyItem.content) {
            messages.push({
              id: `lex-${conversationId}-${messageIndex}`,
              sender: 'lex',
              message: historyItem.content,
              timestamp: new Date(historyItem.timestamp || Date.now()),
              metadata: historyItem.metadata
            });
          }
        }
      }

      console.log('✅ [UniversalLexClient] Loaded specific conversation:', conversationId, 'with', messages.length, 'messages');
      return messages;

    } catch (error) {
      console.error('🚨 [UniversalLexClient] Error loading specific conversation:', error);
      return [];
    }
  }

  /**
   * Load conversation history by documentId + sessionId
   * Returns the resolved conversationId and mapped messages
   */
  async loadConversationBySession(
    documentId: string,
    sessionId: string,
    authToken: string
  ): Promise<{ conversationId: string | null; messages: LexMessage[] }> {
    if (!documentId || !sessionId || !authToken) {
      console.warn('🚨 [UniversalLexClient] Cannot load by session: missing parameters');
      return { conversationId: null, messages: [] };
    }

    try {
      console.log('📜 [UniversalLexClient] Loading conversation by session:', { documentId, sessionId });

      const response = await fetch(
        `${this.baseUrl}/lex-agent/document/${documentId}/session/${sessionId}/history`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (!response.ok) {
        console.warn('🚨 [UniversalLexClient] Failed to load by session:', response.status);
        return { conversationId: null, messages: [] };
      }

      const data = await response.json();
      const resolvedConversationId: string | null = data?.data?.conversationId || null;
      const history: any[] = Array.isArray(data?.data?.conversationHistory)
        ? data.data.conversationHistory
        : [];

      const messages: LexMessage[] = [];
      let messageIndex = 0;
      for (const item of history) {
        messageIndex++;
        if (item.type === 'intent' && item.content) {
          messages.push({
            id: `user-${resolvedConversationId ?? sessionId}-${messageIndex}`,
            sender: 'user',
            message: item.content,
            timestamp: new Date(item.timestamp || Date.now()),
            metadata: { intent: item.category, confidence: item.confidence, ...(item.metadata || {}) }
          });
        }
        if (item.type === 'response' && item.content) {
          messages.push({
            id: `lex-${resolvedConversationId ?? sessionId}-${messageIndex}`,
            sender: 'lex',
            message: item.content,
            timestamp: new Date(item.timestamp || Date.now()),
            metadata: item.metadata
          });
        }
      }

      console.log('✅ [UniversalLexClient] Loaded by session:', { resolvedConversationId, messages: messages.length });
      return { conversationId: resolvedConversationId, messages };

    } catch (error) {
      console.error('🚨 [UniversalLexClient] Error loading by session:', error);
      return { conversationId: null, messages: [] };
    }
  }

  /**
   * Switch to a session using documentId + sessionId. Optionally provide a fallback conversationId.
   */
  async switchToSession(
    documentId: string,
    sessionId: string,
    authToken: string,
    fallbackConversationId?: string
  ): Promise<LexMessage[]> {
    console.log('🔄 [UniversalLexClient] Switching to session:', { documentId, sessionId, fallbackConversationId });

    const { conversationId: resolvedConversationId, messages } = await this.loadConversationBySession(
      documentId,
      sessionId,
      authToken
    );

    // If we resolved a conversationId, bind it to the client for subsequent turns
    if (resolvedConversationId) {
      this.setLoadedSession(sessionId, resolvedConversationId);
    }

    // If no messages were retrieved, attempt a fallback using provided conversationId
    if (messages.length === 0 && fallbackConversationId) {
      const fallbackMessages = await this.loadSpecificConversation(fallbackConversationId, authToken);
      if (fallbackMessages.length > 0) {
        this.setLoadedSession(sessionId, fallbackConversationId);
        return fallbackMessages;
      }
    }

    return messages;
  }

  /**
   * Switch module context (e.g., from Documents to Matters)
   * Maintains conversation continuity while updating context
   */
  switchModule(newContext: LexContext): void {
    const oldApplication = this.currentContext?.application;
    
    this.setContext(newContext);
    
    console.log('🔄 [UniversalLexClient] Module switch:', {
      from: oldApplication,
      to: newContext.application,
      conversationId: this.conversationId
    });
  }

  /**
   * Get current conversation ID
   */
  getConversationId(): string | null {
    return this.conversationId;
  }

  /**
   * Set conversation ID for loading specific conversations
   */
  setConversationId(conversationId: string): void {
    this.conversationId = conversationId;
    console.log('🔄 [UniversalLexClient] Set conversation ID:', conversationId);
  }

  /**
   * Set both DB session (for persistence) and Redis conversation (for transcript) when user loads from history
   */
  setLoadedSession(sessionId: string, conversationId: string): void {
    this.conversationId = conversationId;
    if (this.currentContext) {
      this.currentContext.sessionId = sessionId;
    }
    console.log('🔄 [UniversalLexClient] Loaded session set:', { sessionId, conversationId });
  }

  /**
   * Switch to a loaded conversation session
   * This destroys the current session and switches to the loaded one
   */
  async switchToConversation(sessionId: string, conversationId: string, authToken: string): Promise<LexMessage[]> {
    console.log('🔄 [UniversalLexClient] Switching to conversation:', { sessionId, conversationId });
    
    // Set the new conversation ID as active
    this.conversationId = conversationId;
    
    // Load the conversation history
    const loadedMessages = await this.loadSpecificConversation(conversationId, authToken);
    
    console.log('✅ [UniversalLexClient] Successfully switched to conversation:', conversationId, 'with', loadedMessages.length, 'messages');
    
    return loadedMessages;
  }

  /**
   * Get current context
   */
  getCurrentContext(): LexContext | null {
    return this.currentContext;
  }

  /**
   * Start a fresh conversation (clears context but maintains module)
   */
  startFreshConversation(): void {
    if (this.currentContext) {
      this.conversationId = this.generateConversationId(this.currentContext, true);
      console.log('🆕 [UniversalLexClient] Started fresh conversation:', this.conversationId);
    }
  }

  /**
   * Generate conversation ID based on context for continuity
   * Different contexts get different conversation IDs to maintain proper context
   */
  private generateConversationId(context: LexContext, fresh: boolean = false, userToken?: string): string {
    // Extract user ID from JWT token
    let userId = 'user'; // fallback
    if (userToken) {
      try {
        const payload = JSON.parse(atob(userToken.split('.')[1]));
        userId = payload.sub || 'user'; // Use the JWT 'sub' (subject) field
      } catch (error) {
        console.warn('🚨 [UniversalLexClient] Failed to parse JWT token:', error);
      }
    }
    
    const timestamp = fresh ? Date.now() : '';
    
    // Generate conversation ID based on context to maintain continuity
    // Match backend pattern: user-{userId}-{application}-{specific}
    if (context.documentId) {
      // Document-specific conversation with document ID
      return `user-${userId}-document-${context.documentId}-active${timestamp ? `-${timestamp}` : ''}`;
    } else if (context.matterId) {
      // Matter-specific conversation  
      return `user-${userId}-matters-active${timestamp ? `-${timestamp}` : ''}`;
    } else if (context.clientId) {
      // Client-specific conversation
      return `user-${userId}-clients-active${timestamp ? `-${timestamp}` : ''}`;
    } else {
      // Application-level conversation
      return `user-${userId}-${context.application}-${fresh ? 'fresh-' + timestamp : 'active'}`;
    }
  }

  /**
   * Health check for the Universal Lex Orchestrator
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET'
      });
      return response.ok;
    } catch (error) {
      console.error('🚨 [UniversalLexClient] Health check failed:', error);
      return false;
    }
  }
}

// Singleton instance for use across the application
export const universalLexClient = new UniversalLexClient();

// Types are already exported above with their definitions